import axios from "axios"
import { ReactNode, useCallback, useContext, useMemo } from "react"
import { store } from "../app/store"
import { setLoading } from "../app/slices/loaderSlice"
import AuthContext from "./AuthProvider"
import {
  removePendingRequest,
  setPendingRequests,
} from "../app/slices/pendingRequestsSlice"
import { setBeError } from "../app/slices/beErrorSlice"

interface AxiosProviderProps {
  children: ReactNode
}

export const AxiosProvider = (props: AxiosProviderProps) => {
  const authContext = useContext(AuthContext)

  const onRequest = useCallback((request: any) => {
    const token = sessionStorage.getItem("access_token")
    const user = store.getState().selectedProfile.selectedProfileId

    store.dispatch(setLoading(true))
    store.dispatch(setPendingRequests(request.url))

    /**
     * A /config és a /token végpontokra nem szabad rárakni az Authorization headert.
     */
    if (request.url?.includes("/config") || request.url?.includes("/token")) {
      return request
    }

    /**
     * Minden kérésre rá kell rakni az Authorization headert abban az esetben ha van token a storage-ben.
     */
    if (token) {
      request.headers["Authorization"] = `Bearer ${token}`
    }

    /**
     * Minden kérésre rá kell rakni az User headert abban az esetben ha már van kiválasztott User.
     */
    if (user) {
      request.headers["User"] = user
    }

    return request
  }, [])

  const onRequestError = useCallback((error: any) => {
    return Promise.reject(
      error instanceof Error ? error : new Error(String(error)),
    )
  }, [])

  const onResponse = useCallback(
    (response: any) => {
      store.dispatch(removePendingRequest(response.config.url))
      const count = store.getState().pendingRequests.requests?.length

      if (count === 0) {
        store.dispatch(setLoading(false))
      }

      if (response.status === 200 && response.data.header?.status === "ERROR") {
        console.error("Háttérrendszeri hiba: " + response.config.url)
        const err = new Error("Háttérrendszeri hiba: " + response.config.url)
        Object.assign(err, {
          response: response,
          status: 500,
          url: response.config?.url,
        })
        return Promise.reject(err)
      }

      return response
    },
    [
      /*authContext*/
    ],
  )

  const onResponseError = useCallback(
    (error: any) => {
      store.dispatch(setLoading(false))

      if (error?.config?.url) {
        store.dispatch(removePendingRequest(error.config.url))
      }

      if (error.response?.status === 401 || error.response?.status === 403) {
        authContext.logout()
      }

      const showCommonErrorMessage = !error?.url?.includes(
        "onlinerectificationfinished",
      )

      if (showCommonErrorMessage) {
        store.dispatch(setBeError(true))
      }

      return Promise.reject(
        error instanceof Error ? error : new Error(String(error)),
      )
    },
    [authContext],
  )

  useMemo(() => {
    axios.interceptors.request.use(onRequest, onRequestError)
    axios.interceptors.response.use(onResponse, onResponseError)
  }, [onRequest, onRequestError, onResponse, onResponseError])

  return <>{props.children}</>
}
